import { useState } from 'react';

import { IdentityWizardLayout } from '@identity/presentation/components/base';

import { RecoveryEmailView } from './RecoveryEmailView';
import { RecoverySentView } from './RecoverySentView';

export type PasswordRecoveryWizardProps = {
  initialEmail: string;
};

/**
 * The first half of the recovery flow: one component with two views.
 *
 * The address it keeps is the last one typed, not the one it started with, so coming back from the
 * notice finds the correction already in the field. The reason that way back exists is a typo, and
 * emptying the field would make the user rewrite the part that was right.
 *
 * Reloading returns to the capture view, which is correct because nothing was saved and asking for a
 * link changed nothing: the old password stays valid until a new one is written.
 *
 * There is no requirement map here and no flow provider. This screen carries no credential of any
 * kind, so there is nothing to read a status with and no slice to pick.
 *
 * @param {PasswordRecoveryWizardProps} props - Component properties
 */
export const PasswordRecoveryWizard = ({ initialEmail }: PasswordRecoveryWizardProps) => {
  const [email, setEmail] = useState(initialEmail);
  const [wasRequested, setWasRequested] = useState(false);

  const onRequested = (requestedEmail: string) => {
    setEmail(requestedEmail);
    setWasRequested(true);
  };

  return (
    <IdentityWizardLayout>
      {wasRequested ? (
        <RecoverySentView email={email} onUseAnotherEmail={() => setWasRequested(false)} />
      ) : (
        <RecoveryEmailView initialEmail={email} onRequested={onRequested} />
      )}
    </IdentityWizardLayout>
  );
};
